import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEmbarques } from "@/hooks/useEmbarques";
import { useProveedores } from "@/hooks/useProveedores";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Ship, Plus, Loader2, Sparkles, FlaskConical } from "lucide-react";
import { ClienteSelectField } from "@/components/ClienteSelectField";
import { ProveedorSelectField } from "@/components/ProveedorSelectField";
import { useProformaSimulations } from "@/hooks/useProformaSimulations";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | null;
}

export function AssignEmbarqueModal({ open, onOpenChange, documentId }: Props) {
  const [tab, setTab] = useState("existing");
  const [selectedEmbarqueId, setSelectedEmbarqueId] = useState("");
  const [numeroDo, setNumeroDo] = useState("");
  const [proveedorId, setProveedorId] = useState("");
  const [emisorOcr, setEmisorOcr] = useState(""); // texto crudo del documento, para pre-buscar
  const [receptorOcr, setReceptorOcr] = useState(""); // texto crudo del receptor (cliente)
  const [clienteId, setClienteId] = useState("");
  const [fechaLlegada, setFechaLlegada] = useState("");
  const [saving, setSaving] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [aiSuggestedClienteId, setAiSuggestedClienteId] = useState<string | null>(null);
  const [aiSuggestedProveedorId, setAiSuggestedProveedorId] = useState<string | null>(null);

  const [selectedSimulationId, setSelectedSimulationId] = useState("");
  const { data: embarques = [] } = useEmbarques();
  const { data: proveedores = [] } = useProveedores();
  const { data: simulaciones = [] } = useProformaSimulations({ activeOnly: true });
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const reset = () => {
    setSelectedEmbarqueId("");
    setSelectedSimulationId("");
    setNumeroDo("");
    setProveedorId("");
    setEmisorOcr("");
    setReceptorOcr("");
    setClienteId("");
    setFechaLlegada("");
    setTab("existing");
    setSaving(false);
    setAttemptedSubmit(false);
    setAiSuggestedClienteId(null);
    setAiSuggestedProveedorId(null);
  };

  // Cuando se abre el modal con un documento, lee las sugerencias de IA y el OCR crudo
  // para reducir la fricción a un solo clic.
  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("registro_documentos")
        .select("datos_extra, emisor, receptor, proveedor_id")
        .eq("id", documentId)
        .maybeSingle();
      if (cancelled) return;
      const extra = (data?.datos_extra as any) ?? {};
      const suggestedCli = extra._resolvedClienteId as string | undefined;
      // El proveedor puede venir explícito en datos_extra o ya persistido en proveedor_id
      const suggestedProv =
        (extra._resolvedProveedorId as string | undefined) ||
        (data?.proveedor_id as string | undefined) ||
        undefined;
      // ── Fallback robusto: la IA puede dejar el receptor/emisor en la columna o en datos_extra
      // bajo distintos alias (bill_to, comprador, cliente, vendor, etc.). Nunca dejes el campo vacío
      // si hay cualquier rastro del nombre extraído por OCR.
      const emisorTxt = (
        data?.emisor ||
        extra.emisor ||
        extra.vendor ||
        extra.proveedor ||
        extra.shipper ||
        ""
      ).toString().trim();
      const receptorTxt = (
        data?.receptor ||
        extra.receptor ||
        extra.bill_to ||
        extra.cliente ||
        extra.comprador ||
        extra.consignee ||
        ""
      ).toString().trim();

      // Setea SIEMPRE, incluso si no hay _resolvedClienteId — el combobox usa esto como defaultSearch
      setEmisorOcr(emisorTxt);
      setReceptorOcr(receptorTxt);

      if (suggestedCli) {
        setAiSuggestedClienteId(suggestedCli);
        setClienteId(suggestedCli);
        setTab("new"); // Lleva al operador directo a "Crear y Asignar"
      }
      if (suggestedProv) {
        setAiSuggestedProveedorId(suggestedProv);
        setProveedorId(suggestedProv);
        setTab("new");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, documentId]);

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const assignToEmbarque = async (embarqueId: string) => {
    if (!documentId) return;
    const { error } = await supabase
      .from("registro_documentos")
      .update({ embarque_id: embarqueId } as any)
      .eq("id", documentId);
    if (error) throw error;
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
  };

  const handleAssignExisting = async () => {
    if (!selectedEmbarqueId) {
      toast({ title: "Selecciona un embarque", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await assignToEmbarque(selectedEmbarqueId);
      toast({ title: "✅ Documento asignado al embarque" });
      invalidateAll();
      handleClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndAssign = async () => {
    setAttemptedSubmit(true);
    if (!numeroDo.trim()) {
      toast({ title: "El número DO es obligatorio", variant: "destructive" });
      return;
    }
    if (!clienteId) {
      toast({
        title: "Cliente requerido",
        description: "Asigna el cliente importador para que el embarque sea visible en el Portal B2B.",
        variant: "destructive",
      });
      return;
    }
    // ── Guardrail: bloquea falso guardado de proveedor ──
    // Si el OCR detectó un emisor pero el operador no lo vinculó al directorio,
    // no permitas guardar un embarque con proveedor_id=null y proveedor=null.
    if (!proveedorId && emisorOcr) {
      toast({
        title: "Vincula el proveedor",
        description: `El documento dice "${emisorOcr}". Selecciónalo del directorio o créalo desde el combobox.`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      if (!orgId) throw new Error("No organization context");
      const selectedProv = proveedores.find((p: any) => p.id === proveedorId);
      const { data, error } = await supabase
        .from("embarques")
        .insert({
          numero_do: numeroDo.trim(),
          proveedor: selectedProv?.nombre || null,
          proveedor_id: proveedorId || null,
          cliente_id: clienteId,
          fecha_llegada_estimada: fechaLlegada || null,
          user_id: (await supabase.auth.getUser()).data.user!.id,
          org_id: orgId,
        } as any)
        .select("id")
        .single();
      if (error) throw error;

      await assignToEmbarque(data.id);

      // ── Frente C: aprendizaje en cascada de alias de cliente ──
      // Lee el receptor del documento que se está asignando y guárdalo
      // como alias para que el próximo PDF con el mismo "Bill To" lo auto-vincule.
      if (documentId && orgId && clienteId) {
        const { data: docRow } = await supabase
          .from("registro_documentos")
          .select("receptor")
          .eq("id", documentId)
          .maybeSingle();
        const receptorOcr = (docRow?.receptor || "").trim();
        if (receptorOcr) {
          await supabase
            .from("cliente_aliases")
            .insert({ cliente_id: clienteId, nombre_ocr: receptorOcr, org_id: orgId })
            .then(({ error: aliasErr }) => {
              if (aliasErr && !aliasErr.message.toLowerCase().includes("duplicate")) {
                console.warn("No se pudo guardar alias de cliente:", aliasErr.message);
              }
            });
        }
      }

      toast({ title: "✅ Embarque creado y documento asignado" });
      invalidateAll();
      handleClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToSimulation = async () => {
    if (!documentId) return;
    if (!selectedSimulationId) {
      toast({ title: "Selecciona una simulación", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc("attach_document_to_simulation" as never, {
        p_document_id: documentId,
        p_simulation_id: selectedSimulationId,
      } as never);
      if (error) throw error;
      const result = (data as any) ?? {};
      if (result.already_attached) {
        toast({ title: "Documento ya estaba vinculado a esta simulación" });
      } else {
        toast({
          title: "✅ Documento vinculado a la simulación",
          description: `${result.items_added ?? 0} ítem(s) y ${result.gastos_added ?? 0} gasto(s) añadidos al escenario.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["proforma-simulation", selectedSimulationId] });
      queryClient.invalidateQueries({ queryKey: ["proforma-simulations"] });
      handleClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-hidden grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ship className="h-5 w-5 text-primary" />
            Asignar Documento a un Embarque
          </DialogTitle>
          <DialogDescription>
            Vincula este documento a un Delivery Order existente o crea uno nuevo.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full min-w-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="existing">Existente</TabsTrigger>
            <TabsTrigger value="new">
              <Plus className="h-3.5 w-3.5 mr-1" />
              Crear DO
            </TabsTrigger>
            <TabsTrigger value="simulation">
              <FlaskConical className="h-3.5 w-3.5 mr-1" />
              Simulación
            </TabsTrigger>
          </TabsList>

          <TabsContent value="existing" className="space-y-4 pt-2">
            <div className="space-y-2 min-w-0">

              <Label>Embarque (DO)</Label>
              <Select value={selectedEmbarqueId} onValueChange={setSelectedEmbarqueId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un embarque..." />
                </SelectTrigger>
                <SelectContent>
                  {embarques.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.numero_do}{e.proveedor ? ` — ${e.proveedor}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <TabsContent value="new" className="space-y-4 pt-2 min-w-0">
            <div className="space-y-2">
              <Label>Número DO *</Label>
              <Input value={numeroDo} onChange={(e) => setNumeroDo(e.target.value)} placeholder="DO-2026-001" />
            </div>
            <div className="space-y-2 min-w-0">

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label>
                  Cliente Importador <span className="text-destructive">*</span>
                </Label>
                {aiSuggestedClienteId && clienteId === aiSuggestedClienteId && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 animate-fade-in"
                    title="Auto-detectado por la IA a partir del receptor del documento"
                  >
                    <Sparkles className="h-3 w-3" />
                    Sugerencia de IA aplicada
                  </span>
                )}
              </div>
              <ClienteSelectField
                value={clienteId}
                onValueChange={(v) => {
                  setClienteId(v);
                  // Si el operador cambia manualmente, ocultamos el badge
                  if (v !== aiSuggestedClienteId) setAiSuggestedClienteId(null);
                }}
                required
                invalid={attemptedSubmit && !clienteId}
                defaultSearch={receptorOcr}
              />
              <p className="text-[11px] text-muted-foreground">
                Obligatorio para que el embarque aparezca en el Portal B2B del cliente.
              </p>
              {receptorOcr && !clienteId && (
                <p className="text-[11px] text-muted-foreground">
                  Documento OCR: <span className="font-medium">{receptorOcr}</span> — sin coincidencia automática.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label>Proveedor</Label>
                {aiSuggestedProveedorId && proveedorId === aiSuggestedProveedorId && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 animate-fade-in"
                    title="Auto-detectado por la IA a partir del emisor del documento"
                  >
                    <Sparkles className="h-3 w-3" />
                    Sugerencia de IA aplicada
                  </span>
                )}
              </div>
              <ProveedorSelectField
                value={proveedorId}
                onValueChange={(v) => {
                  setProveedorId(v);
                  if (v !== aiSuggestedProveedorId) setAiSuggestedProveedorId(null);
                }}
                defaultSearch={emisorOcr}
                invalid={attemptedSubmit && !proveedorId && !!emisorOcr}
              />
              {emisorOcr && !proveedorId && (
                <p className="text-[11px] text-muted-foreground">
                  Documento OCR: <span className="font-medium">{emisorOcr}</span> — sin coincidencia automática.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Fecha Estimada de Llegada</Label>
              <Input type="date" value={fechaLlegada} onChange={(e) => setFechaLlegada(e.target.value)} />
            </div>
          </TabsContent>

          <TabsContent value="simulation" className="space-y-4 pt-2 min-w-0">
            <div className="space-y-2 min-w-0">
              <Label>Simulación Pro-Forma activa</Label>
              <Select value={selectedSimulationId} onValueChange={setSelectedSimulationId}>
                <SelectTrigger>
                  <SelectValue placeholder={simulaciones.length ? "Selecciona una simulación..." : "No hay simulaciones activas"} />
                </SelectTrigger>
                <SelectContent>
                  {simulaciones.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{s.nombre}</span>
                        <span className="text-[10px] text-muted-foreground">
                          actualizada {formatDistanceToNow(new Date(s.updated_at), { addSuffix: true, locale: es })}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Los ítems y gastos del documento se añadirán al escenario como "items_nuevos" y "gastos_nuevos". El documento original no se modifica.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleClose} className="text-muted-foreground">
            Omitir por ahora
          </Button>
          <Button
            onClick={
              tab === "existing"
                ? handleAssignExisting
                : tab === "simulation"
                ? handleAssignToSimulation
                : handleCreateAndAssign
            }
            disabled={saving}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {tab === "existing" ? "Asignar" : tab === "simulation" ? "Asignar a simulación" : "Crear y Asignar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
